import React, { useState, useEffect, useRef, useCallback } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import Can from '@shared/permissions/Can';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import {
  Form,
  Input,
  Button,
  message,
  Space,
  Select,
  DatePicker,
  Spin,
  Divider,
  Row,
  Col,
  Tag
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { toDayjsOrNull } from '@utils/dateTime';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import InboundItemsForm from './InboundItemsForm';

const { TextArea } = Input;

interface InventoryTypeOption {
  id: string;
  name?: string;
  code?: string;
  enabled?: boolean;
  [key: string]: unknown;
}

interface WarehouseOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface InventoryAttributeDefinition {
  id?: string;
  name: string;
  label?: string;
  data_type?: string;
  [key: string]: unknown;
}

interface InboundLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null } | null;
  [key: string]: unknown;
}

interface InboundEditProps {
  // useInboundService 是 .js hook，service 物件方法多樣（create/update/getById/suggestName/...），
  // 鏡像 InboundShow.tsx 的 fall back 為 any。
  useInboundService: () => any;
  useProductService: () => any;
  // Supplier service：載入下拉清單給「供應商」Select 使用；
  // wrapper 在 operator 端透過 useOperatorSupplierService bind currentMerchant 過濾範圍後傳入。
  supplierService: any;
  role?: 'admin' | 'operator';
  operatorWarehouseName?: string | null;
  warehouses?: WarehouseOption[];
  defaultWarehouseId?: string | null;
  // merchant-scoped reference data injected by wrapper — keeps shared component
  // merchant-agnostic and lets the wrapper align it to the loaded record's merchant.
  inventoryTypes?: InventoryTypeOption[];
  allInventoryTypes?: InventoryTypeOption[];
  inventoryAttributeDefinitions?: InventoryAttributeDefinition[];
  // Called by `loadData` once the inbound is fetched. Wrapper uses this to sync
  // OperatorDataContext.currentMerchant to the record's merchant before any
  // merchant-scoped dropdown renders.
  onInboundLoaded?: (inbound: InboundLoadedPayload) => void;
  // True while the wrapper is still switching context to the record's merchant.
  // Render-gated to avoid the form briefly showing the previous merchant's options.
  merchantDataLoading?: boolean;
}

/**
 * 通用 Inbound 編輯視圖組件
 */
const InboundEdit: React.FC<InboundEditProps> = ({
  useInboundService,
  useProductService,
  supplierService,
  role = 'admin',
  operatorWarehouseName,
  warehouses = [],
  defaultWarehouseId,
  inventoryTypes = [],
  allInventoryTypes,
  inventoryAttributeDefinitions = [],
  onInboundLoaded,
  merchantDataLoading = false,
}) => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  // allInventoryTypes 用來在 loadData 時把 inventory_type_id 對應到名稱（含已停用），
  // 沒傳就以 inventoryTypes 兜底（admin 端 enabled/all 相同）。
  const inventoryTypeLookupSource = allInventoryTypes ?? inventoryTypes;
  const addItemRef = useRef<((value: Record<string, unknown>) => void) | null>(null);

  const handleAddItem = useCallback(() => {
    if (!addItemRef.current) return;
    const defaultInventoryType = inventoryTypes[0]
      ? { value: inventoryTypes[0].id, label: inventoryTypes[0].name }
      : null;

    addItemRef.current({
      key: Date.now(),
      product_id: null,
      sku: '',
      name: '',
      inventory_type_id: defaultInventoryType,
      batch: '',
      expiration_date: null,
      scheduled_quantity: 1,
      custom_attributes: {}
    });
    setTimeout(() => {
      const cards = document.querySelectorAll('.product-item-card');
      cards[cards.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, [inventoryTypes]);

  const service = useInboundService();
  const productService = useProductService();

  const [suppliers, setSuppliers] = useState<Array<{ id: string; name: string }>>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  const loadSuppliers = useCallback(async () => {
    try {
      setSuppliersLoading(true);
      const res = await supplierService.getRecords({ page: 1, page_size: 100 });
      if (res.success && res.data) {
        setSuppliers(res.data);
      }
    } catch (error) {
      // 載入失敗只影響下拉填充，UI 仍可手動 reload；保持 silent 避免遮蓋表單主流程錯誤
      console.error('Failed to load suppliers:', error);
    } finally {
      setSuppliersLoading(false);
    }
  }, [supplierService]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const supplierOptions = React.useMemo(
    () => suppliers.map(s => ({ label: s.name, value: s.id })),
    [suppliers]
  );

  const handleSuggestName = async () => {
    try {
      setSuggesting(true);
      const res = await service.suggestName();
      if (res.success) {
        form.setFieldsValue({ name: res.data.name });
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setSuggesting(false);
    }
  };

  // 產品搜尋函數
  useEffect(() => {
    loadData();
  }, [id]);

  // 將新建模式預設值抽成 callback，以便 resetFields 後重新套用
  const applyNewModeDefaults = useCallback(() => {
    if (!isNew) return;
    form.setFieldsValue({
      inbound_items_attributes: [],
      ...(role === 'admin' && defaultWarehouseId ? { warehouse_id: defaultWarehouseId } : {})
    });
  }, [isNew, role, defaultWarehouseId, form]);

  // Admin 新增模式：當 defaultWarehouseId 就緒後設定預設倉庫
  useEffect(() => {
    if (role === 'admin' && isNew && defaultWarehouseId) {
      const current = form.getFieldValue('warehouse_id');
      if (!current) {
        form.setFieldsValue({ warehouse_id: defaultWarehouseId });
      }
    }
  }, [defaultWarehouseId]);
  const loadData = async () => {
    try {
      setDataLoading(true);
      if (id) {
        const response = await service.getById(id);
        if (response.success) {
          const inbound = response.data;
          // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
          // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared
          // 本身不再讀 / 寫 merchant context，保持 merchant-agnostic。
          onInboundLoaded?.(inbound);
          // 處理 items - 添加 key 用於前端渲染
          const items = (inbound.items || []).map((item: Record<string, any>) => {
            // Construct product object
            const productId = item.product_id;
            const productLabel = item.product_name || item.sku || productId; // Fallback

            // Construct inventory type object
            const inventoryTypeId = item.inventory_type_id;
            let inventoryTypeLabel = item.inventory_type_name;

            if (!inventoryTypeLabel && inventoryTypeId && inventoryTypeLookupSource.length > 0) {
              const type = inventoryTypeLookupSource.find(t => t.id === inventoryTypeId);
              if (type) inventoryTypeLabel = type.name;
            }

            inventoryTypeLabel = inventoryTypeLabel || inventoryTypeId; // Fallback

            return {
              ...item,
              key: item.id || Date.now() + Math.random(),
              product_id: productId
                ? {
                  value: productId,
                  label: productLabel
                }
                : null,
              inventory_type_id: inventoryTypeId
                ? {
                  value: inventoryTypeId,
                  label: inventoryTypeLabel
                }
                : null,
              product_sku: item.product_sku || item.sku, // Ensure these are available for ProductItemCard fallback
              product_name: item.product_name || item.name,
              scheduled_quantity: item.scheduled_quantity,
              expiration_date: toDayjsOrNull(item.expiration_date),
              manufacturing_date: toDayjsOrNull(item.manufacturing_date)
            };
          });
          form.setFieldsValue({
            ...inbound,
            eta: toDayjsOrNull(inbound.eta),
            inbound_items_attributes: items
          });
        } else {
          handleApiError(response, { t, fallbackMessage: t('common.error.record_not_found') });
          goBack();
        }
      } else {
        // 新增模式，初始化 items 為空陣列
        form.setFieldsValue({
          inbound_items_attributes: [],
          ...(role === 'admin' && defaultWarehouseId ? { warehouse_id: defaultWarehouseId } : {})
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Load data error:', error);
    } finally {
      setDataLoading(false);
    }
  };
  // 組合送出 payload，給兩個提交按鈕共用
  const buildPayload = (values: Record<string, any>) => {
    // Process items: extract only permitted fields
    const items = (values.inbound_items_attributes || []).map((item: Record<string, any>) => {
      const productId = item.product_id?.value || item.product_id;
      const inventoryTypeId = item.inventory_type_id?.value || item.inventory_type_id;

      const customAttributes =
        item.custom_attributes && typeof item.custom_attributes === 'object'
          ? item.custom_attributes
          : null;

      return {
        id: item.id,
        product_id: productId,
        inventory_type_id: inventoryTypeId,
        batch: item.batch,
        expiration_date: item.expiration_date ? dayjs(item.expiration_date).format('YYYY-MM-DD') : null,
        manufacturing_date: item.manufacturing_date ? dayjs(item.manufacturing_date).format('YYYY-MM-DD') : null,
        scheduled_quantity: item.scheduled_quantity || 0,
        received_quantity: item.received_quantity || 0,
        putaway_quantity: item.putaway_quantity || 0,
        custom_attributes: customAttributes,
        _destroy: item._destroy
      };
    });

    // Construct top-level payload with permitted fields
    const inboundPayload: Record<string, unknown> = {
      name: values.name,
      inbound_type: values.inbound_type,
      eta: values.eta ? (values.eta as Dayjs).format('YYYY-MM-DD') : null,
      supplier_id: values.supplier_id ?? null,
      extra_name: values.extra_name,
      note: values.note,
      inbound_items_attributes: items
    };

    // Only include merchant_id if creating a new record
    if (isNew && values.merchant_id) {
      inboundPayload.merchant_id = values.merchant_id;
    }

    // Include warehouse_id for admin creating a new inbound
    if (role === 'admin' && isNew && values.warehouse_id) {
      inboundPayload.warehouse_id = values.warehouse_id;
    }

    return inboundPayload;
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  const submitValues = async (values: Record<string, any>) => {
    const payload = buildPayload(values);
    return isNew ? await service.create(payload) : await service.update(id, payload);
  };

  // 將 response.errors 套回欄位（保留既有錯誤處理邏輯）
  const applyFieldErrors = (response: { errors?: Record<string, unknown> }) => {
    if (response.errors) {
      const fieldEntries = Object.entries(response.errors);
      form.setFields(fieldEntries.map(([name, errors]) => ({
        name,
        errors: (Array.isArray(errors) ? errors : [errors]).map((e: any) =>
          t(`common.validation.${e.message || e}`, { defaultValue: e.message || e })
        )
      })));
    }
  };

  // 主要提交：成功後導向 show 頁（新建與編輯皆適用）
  const handleSubmit = async (values: Record<string, any>) => {
    setLoading(true);
    try {
      const response = await submitValues(values);
      if (response.success) {
        message.success(t(`common.message.save_success`));
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
        // React Router basename 會自動補上 /operator 或 /admin 前綴
        const recordId = response.data?.id || id;
        navigate(`/inbounds/${recordId}`, { replace: true });
      } else {
        applyFieldErrors(response);
        handleApiError(response, {
          t,
          fallbackMessage: t(`common.error.save_failed`)
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);
        if (response.success) {
          message.success(t(`common.message.save_success`));
          // 重置表單，然後重新套用新建模式的預設值（倉庫、空 items）
          form.resetFields();
          applyNewModeDefaults();
        } else {
          applyFieldErrors(response);
          handleApiError(response, {
            t,
            fallbackMessage: t(`common.error.save_failed`)
          });
        }
      } catch (error) {
        handleNetworkError(error, { t });
        console.error('Submit error:', error);
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };
  const inboundTypeOptions = [
    { label: t(`enums.inbound.type.receiving`), value: 'receiving' },
    { label: t(`enums.inbound.type.sales_return`), value: 'sales_return' },
    { label: t(`enums.inbound.type.production`), value: 'production' },
  ];
  const basePath = role === 'operator' ? '/operator' : '/admin';

  // Render-gate: sync 期間（dataLoading 抓 record；merchantDataLoading 切 context 到
  // record 的 merchant）整段 form 不能 mount，避免 inventory_type / supplier 下拉
  // 與 inventoryAttributeDefinitions 先用 header merchant 的清單 render。
  if (dataLoading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        basePath={basePath}
        items={['inbounds', 'inbounds.list', { label: id ? t('shared.inbound_form.title_edit') : t('shared.inbound_form.title_new') }]}
        extra={
          role === 'operator' && !id && operatorWarehouseName ? (
            <Tag color="green">{operatorWarehouseName}</Tag>
          ) : null
        }
      />
      <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            {(() => {
              const showWarehouseField = role === 'admin' && warehouses.length > 0;
              return (
                <Row gutter={16}>
                  {showWarehouseField && (
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={t('shared.inbounds.form.warehouse')}
                        name="warehouse_id"
                        rules={[{ required: true, message: t('shared.inbounds.form.warehouse_required') }]}
                      >
                        <Select
                          placeholder={t('shared.inbounds.form.warehouse_placeholder')}
                          options={warehouses.map(w => ({ label: w.name, value: w.id }))}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col xs={24} md={showWarehouseField ? 12 : 24}>
                    <Form.Item
                      label={t(`shared.inbounds.form.name`)}
                      required
                    >
                      <Space.Compact style={{ width: '100%' }}>
                        <Form.Item
                          name="name"
                          noStyle
                          rules={[{ required: true, message: t(`shared.inbounds.form.name_required`) }]}
                        >
                          <Input data-testid="inbound-name-input" placeholder={t(`shared.inbounds.form.name_placeholder`)} />
                        </Form.Item>
                        {!id && (
                          <Can requiredPermission="inbounds:read">
                            <Button
                              onClick={handleSuggestName}
                              loading={suggesting}
                              disabled={loading}
                            >
                              {t('shared.inbounds.form.suggest_name')}
                            </Button>
                          </Can>
                        )}
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                </Row>
              );
            })()}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t(`shared.inbounds.form.inbound_type`)}
                  name="inbound_type"
                  rules={[{ required: true, message: t(`shared.inbounds.form.inbound_type_required`) }]}
                  tooltip={t(`shared.inbounds.form.inbound_type_tooltip`)}
                  extra={t(`shared.inbounds.form.inbound_type_hint`)}
                >
                  <Select
                    data-testid="inbound-type-select"
                    showSearch
                    placeholder={t(`shared.inbounds.form.inbound_type_placeholder`)}
                    options={inboundTypeOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t(`shared.inbounds.form.eta`)}
                  required
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item
                      name="eta"
                      noStyle
                      rules={[{ required: true, message: t(`shared.inbounds.form.eta_required`) }]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        placeholder={t('common.placeholder.select_date')}
                        disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
                      />
                    </Form.Item>
                    <Button data-testid="inbound-eta-today-btn" onClick={() => form.setFieldsValue({ eta: dayjs() })}>
                      {t('common.button.today')}
                    </Button>
                  </Space.Compact>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('shared.inbounds.form.supplier')}
                  name="supplier_id"
                >
                  <Select
                    data-testid="inbound-supplier-select"
                    showSearch
                    allowClear
                    placeholder={t('shared.inbounds.form.supplier_placeholder')}
                    options={supplierOptions}
                    loading={suppliersLoading}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t(`shared.inbounds.form.extra_name`)}
                  name="extra_name"
                  tooltip={t(`shared.inbounds.form.extra_name_tooltip`)}
                >
                  <Input placeholder={t(`shared.inbounds.form.extra_name_placeholder`)} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label={t(`shared.inbounds.form.note`)}
              name="note"
            >
              <TextArea rows={4} placeholder={t(`shared.inbounds.form.note_placeholder`)} />
            </Form.Item>
            <Divider>{t(`shared.inbounds.form.items_section`)}</Divider>

            <Form.List
              name="inbound_items_attributes"
              rules={[
                {
                  validator: async (_, items) => {
                    if (!items || items.length === 0) {
                      return Promise.reject(new Error(t(`shared.inbounds.form.items_required`)));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              {(fields, { add, remove }) => {
              addItemRef.current = add as (value: Record<string, unknown>) => void;
              return (
                <InboundItemsForm
                  fields={fields}
                  add={add}
                  remove={remove}
                  form={form}
                  productService={productService}
                  inventoryTypes={inventoryTypes}
                  allInventoryTypes={inventoryTypeLookupSource}
                  inventoryAttributeDefinitions={inventoryAttributeDefinitions}
                  role={role}
                />
              );
            }}
            </Form.List>

            <FloatingActionBarSpacer />
          </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        extraButtons={
          <>
            <Button
              data-testid="inbound-add-item-btn"
              icon={<PlusOutlined />}
              onClick={handleAddItem}
            >
              {t('shared.inbounds.form.add_item')}
            </Button>
            {isNew && (
              <Button
                loading={savingAndAdding}
                disabled={loading}
                onClick={handleSaveAndAddAnother}
              >
                {t('common.button.save_and_add_another')}
              </Button>
            )}
          </>
        }
        submitText={t(isNew ? 'common.button.create' : 'common.button.save')}
      />
    </div>
  );
};

export default InboundEdit;
